"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.EmailForm = void 0;
var _zod = require("@hookform/resolvers/zod");
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _auth = require("@ori/auth");
var _commonFormFields = require("@ori/common-form-fields");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _axios = _interopRequireDefault(require("axios"));
var _react = require("react");
var _reactHookForm = require("react-hook-form");
var _zod2 = require("zod");
var _defaultTranslations = require("../constants/defaultTranslations");
var _contexts = require("../contexts");
var _useTranslations = require("../hooks/useTranslations");
var _models = require("../models");
var _utils = require("../utils");
var _ActionButtons = require("./ActionButtons");
var _ConfirmDeleteDialog = require("./ConfirmDeleteDialog");
var _Loader = require("./Loader");
var _Localization = require("./Localization");
var _ValidationSummary = require("./ValidationSummary");
var _jsxRuntime = require("react/jsx-runtime");
const TrashcanIcon = (0, _mui.styled)(_icons.Trashcan)(({
  theme
}) => ({
  marginRight: theme.spacing(2),
  fontSize: '26px'
}));
const ButtonDelete = (0, _mui.styled)('div')(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(0)
}));
const EmailForm = props => {
  const {
    hideFormHandler,
    onDeleteEmail,
    sectionType,
    typeEmail,
    emptyField,
    customInputMaxWidth,
    currentCustomerId
  } = props;
  const {
    customerId
  } = (0, _auth.useToken)();
  const selectedCustomerId = currentCustomerId ?? customerId;
  const {
    data: emailData,
    mutate: refreshEmails
  } = (0, _globalApiClientReact.useGetCustomerEmails)(selectedCustomerId, {}, {
    swr: {
      revalidateOnFocus: false
    }
  });
  const {
    data: metadata
  } = (0, _globalApiClientReact.useGetCustomerEmailsMetadata)(selectedCustomerId, {}, {
    swr: {
      revalidateOnFocus: false
    }
  });
  const {
    data: completenessMetadata
  } = (0, _globalApiClientReact.useGetCustomerProfileCompletenessMetadata)(selectedCustomerId, {}, {
    swr: {
      revalidateOnFocus: false
    }
  });
  const {
    formatMessage
  } = (0, _useTranslations.useTranslations)();
  const {
    notificationData
  } = (0, _contexts.useNotificationData)();
  const {
    setNotificationData
  } = notificationData;
  const [confirmOpened, setConfirmOpened] = (0, _react.useState)(false);
  const getSchema = config => {
    let schema = _zod2.z.object({});
    if (!config?.emailAddress?.validation?.isMandatory) {
      schema = _zod2.z.object({
        emailAddress: _zod2.z.string().regex(new RegExp(config?.emailAddress?.validation?.regularExpression ?? ''), formatMessage(_models.TranslationKey.validationRegexEmail)).optional()
      });
    } else {
      schema = _zod2.z.object({
        emailAddress: _zod2.z.string({
          message: formatMessage(_models.TranslationKey.validationRequired)
        }).min(1, {
          message: formatMessage(_models.TranslationKey.validationRequired)
        }).regex(new RegExp(config?.emailAddress?.validation?.regularExpression ?? ''), formatMessage(_models.TranslationKey.validationRegexEmail))
      });
    }
    return schema;
  };
  const currentEmail = emailData?.emails.find(email => !email.isPending)?.emailAddress;
  const tempEmail = emailData?.emails.find(email => email.isPending)?.emailAddress;
  const editingEmail = typeEmail === 'temp' ? tempEmail : currentEmail;
  const defaultValues = {
    emailAddress: editingEmail
  };
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing --- we need or
  const anyEmail = Boolean(currentEmail || tempEmail);
  const availableCompleteness = completenessMetadata?.isAvailable;
  const methods = (0, _reactHookForm.useForm)({
    resolver: (0, _zod.zodResolver)(getSchema(metadata)),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues
  });
  const {
    reset,
    watch,
    handleSubmit,
    setError,
    formState: {
      isValid,
      isDirty,
      isSubmitting,
      isSubmitted
    }
  } = methods;
  const values = watch();
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const globalApi = (0, _globalApiClientReact.useApiClient)();
  const onSubmit = async data => {
    const {
      hideFormHandler: onSubmitSuccess
    } = props;
    try {
      const response = await (0, _globalApiClientReact.createCustomerEmail)(globalApi, selectedCustomerId, {
        emailAddress: data.emailAddress ?? '',
        isPending: false
      });
      const result = new _models.FetchResult(response.status);
      if (result.isSuccess()) {
        setNotificationData({
          isOpen: true,
          message: formatMessage(_models.TranslationKey.infoConfirmDialog),
          type: 'success'
        });
        onSubmitSuccess();
        refreshEmails();
        if (availableCompleteness) {
          await (0, _globalApiClientReact.getCustomerProfileCompleteness)(globalApi, selectedCustomerId);
        }
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- TODO we must fixed and checked correct working in separate story
      if (_axios.default.isAxiosError(error) && error.response?.data) {
        const errorResponse = (0, _utils.transformKeysToCamelCase)(error.response.data);
        (0, _utils.handleValidationErrors)(errorResponse, setError, formatMessage);
      } else {
        console.error('Unknown error create email:', error);
      }
    }
  };
  const cancelClick = () => {
    hideFormHandler();
    reset();
  };
  const isEditable = metadata?.emailAddress?.isEditable;
  const isDeleteEnabled = metadata?.operations?.delete?.isEnabled && anyEmail;
  const buttonDisabled = !isEditable || isSubmitting;
  const handleDeleteEmail = () => {
    setConfirmOpened(true);
  };
  const confirmCanceled = () => {
    setConfirmOpened(false);
  };
  const deleteEmail = async () => {
    try {
      if (editingEmail) {
        const response = await (0, _globalApiClientReact.deleteCustomerEmail)(globalApi, selectedCustomerId, editingEmail);
        const result = new _models.FetchResult(response.status);
        if (result.isSuccess()) {
          if (availableCompleteness) {
            await (0, _globalApiClientReact.getCustomerProfileCompleteness)(globalApi, selectedCustomerId);
          }
          onDeleteEmail(_models.DisplayFeedback.Delete);
          refreshEmails();
        }
        onDeleteEmail();
      }
    } catch (error) {
      setNotificationData({
        isOpen: true,
        message: formatMessage(_models.TranslationKey.cannotUpdate),
        type: 'error'
      });
    }
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_reactHookForm.FormProvider, {
    ...methods,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("form", {
      onSubmit: handleSubmit(onSubmit),
      children: [sectionType === _models.SectionType.Profile ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Typography, {
        paragraph: true,
        variant: "body1",
        align: "center",
        children: tempEmail ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.pendingEmailTitle,
          defaultMessage: _defaultTranslations.defaultTranslations.pendingEmailTitle
        }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.enterEmailTitle,
          defaultMessage: _defaultTranslations.defaultTranslations.enterEmailTitle
        })
      }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_ValidationSummary.ValidationSummary, {}),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing --- we need or
      typeEmail === 'temp' || !typeEmail ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Grid, {
        container: true,
        spacing: 2,
        alignItems: "center",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Grid, {
          size: {
            xs: 12
          },
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
            label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
              id: _models.TranslationKey.newEmail,
              defaultMessage: _defaultTranslations.defaultTranslations.newEmail
            }),
            name: "emailAddress",
            customPlaceholder: formatMessage(_models.TranslationKey.emailPlaceholder),
            autoFocus: emptyField === 'Email',
            inputType: "email",
            keyboardType: "email",
            enableLowercaseLetters: true,
            ...metadata?.emailAddress,
            isMandatory: metadata?.emailAddress?.validation?.isMandatory,
            customMaxWidth: customInputMaxWidth
          })
        })
      }) : null, typeEmail === 'current' ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
        label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.emailTitle,
          defaultMessage: _defaultTranslations.defaultTranslations.emailTitle
        }),
        name: "emailAddress",
        customPlaceholder: formatMessage(_models.TranslationKey.emailPlaceholder),
        autoFocus: emptyField === 'Email',
        inputType: "email",
        keyboardType: "email",
        enableLowercaseLetters: true,
        ...metadata?.emailAddress,
        isMandatory: metadata?.emailAddress?.validation?.isMandatory,
        customMaxWidth: customInputMaxWidth
      }) : null, isDeleteEnabled ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Grid, {
        container: true,
        spacing: 2,
        alignItems: "center",
        justifyContent: "flex-start",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Grid, {
          container: true,
          paddingLeft: 1,
          size: {
            xs: 12
          },
          children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(ButtonDelete, {
            role: "presentation",
            "data-testid": getTestId('delete-email-button'),
            onClick: () => handleDeleteEmail(),
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(TrashcanIcon, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Typography, {
              variant: "body1",
              children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
                id: _models.TranslationKey.deleteEmail,
                defaultMessage: _defaultTranslations.defaultTranslations.deleteEmail
              })
            })]
          })
        })
      }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_ActionButtons.ActionButtons, {
        isTouchedForm: true,
        enableCancel: anyEmail,
        isValid: !buttonDisabled && defaultValues.emailAddress !== values.emailAddress && isValid,
        dataTestIdPrefix: "email",
        handleCancel: cancelClick,
        currentCustomerId: selectedCustomerId,
        isSubmitting,
        isDirty,
        isSubmitted
      }), isSubmitting ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Loader.Loader, {}) : null]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ConfirmDeleteDialog.ConfirmDeleteDialog, {
      opened: confirmOpened,
      handleCancel: confirmCanceled,
      handleConfirm: deleteEmail,
      header: _models.TranslationKey.deleteEmail,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Typography, {
        align: "center",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.confirmEmailDelete,
          defaultMessage: _defaultTranslations.defaultTranslations.confirmEmailDelete
        })
      })
    })]
  });
};
exports.EmailForm = EmailForm;
